import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import Badge from '../../../components/ui/Badge';
import { LockIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  ReferenceDataGeneralList,
  type ReferenceInboxColumn,
} from '../../parameters/ReferenceDataGeneralList';
import { SpareLibraryFormModal } from './SpareLibraryFormModal';
import { moduleConfig } from './config';
import { useSpareLibraries, useSpareLibrary, type SpareLibrary } from './useData';

const List: React.FC = () => {
  const { colors } = useTheme();
  const { mutate } = useSWRConfig();
  const formatDate = moduleConfig.utils.formatDate;
  const [searchParams, setSearchParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<SpareLibrary | null>(null);

  const editIdRaw = searchParams.get('edit');
  const editId = editIdRaw != null && editIdRaw !== '' ? Number(editIdRaw) : null;
  const wantNew = searchParams.get('new') === '1';
  const { data: editFromQuery } = useSpareLibrary(
    editId != null && Number.isFinite(editId) && editId > 0 ? editId : undefined,
  );

  useEffect(() => {
    if (wantNew) {
      setEditItem(null);
      setFormOpen(true);
      return;
    }
    if (editId != null && Number.isFinite(editId) && editId > 0 && editFromQuery) {
      setEditItem(editFromQuery);
      setFormOpen(true);
    }
  }, [wantNew, editId, editFromQuery]);

  const clearFormParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    next.delete('edit');
    setSearchParams(next, { replace: true });
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditItem(null);
    clearFormParams();
  };

  const columns = useMemo<ReferenceInboxColumn<SpareLibrary>[]>(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: (row) => (
          <span className={cn('inline-flex min-w-0 items-center gap-1.5 font-medium', colors.text)}>
            {row.isUsed ? (
              <span
                className="inline-flex shrink-0 text-amber-500"
                title="In use — library items appear on spare requisitions"
              >
                <LockIcon className="h-4 w-4" />
              </span>
            ) : null}
            <span className="truncate">{row.name}</span>
          </span>
        ),
      },
      {
        key: 'isActive',
        header: 'Active',
        className: 'whitespace-nowrap',
        render: (row) => (
          <Badge variant={row.isActive ? 'success' : 'danger'}>{row.isActive ? 'Yes' : 'No'}</Badge>
        ),
      },
      {
        key: 'sortOrder',
        header: 'Order',
        className: 'hidden whitespace-nowrap sm:table-cell',
        render: (row) => (
          <span className={colors.textMuted}>{row.sortOrder != null ? row.sortOrder : ''}</span>
        ),
      },
      {
        key: 'createdAt',
        header: 'Created',
        className: 'hidden whitespace-nowrap lg:table-cell',
        render: (row) => <span className={colors.textMuted}>{formatDate(row.createdAt)}</span>,
      },
    ],
    [colors.text, colors.textMuted, formatDate],
  );

  return (
    <>
      <ReferenceDataGeneralList
        moduleConfig={moduleConfig}
        useData={useSpareLibraries}
        columns={columns}
        defaultSort={{ key: 'sortOrder', direction: 'asc' }}
        newLabel="New library"
        getRowLabel={(row) => row.name}
        loadingLabel="Loading spare libraries…"
      />
      <SpareLibraryFormModal
        isOpen={formOpen}
        editItem={editItem}
        onClose={closeForm}
        onSaved={() => {
          void mutate(
            (key) => typeof key === 'string' && key.startsWith('/spare-libraries'),
            undefined,
            { revalidate: true },
          );
          closeForm();
        }}
      />
    </>
  );
};

export default List;
